import React from 'react';
import { LeagueFormatSelector, type LeagueFormatType } from '../LeagueFormatSelector';
import { firstSnakePicks, formatLabel } from '../../utils/draftBoard';
import { ordinal } from '../../utils/leagueInsights';
import type { ScoringFormat } from '../../utils/leagueInsights';

interface DraftSettingsProps {
  leagueFormat: LeagueFormatType;
  onFormatChange: (format: LeagueFormatType) => void;
  scoringFormat: ScoringFormat;
  onScoringChange: (scoring: ScoringFormat) => void;
  teamCount: number;
  onTeamCountChange: (count: number) => void;
  draftPosition: number;
  onDraftPositionChange: (position: number) => void;
}

const teamOptions = [8, 10, 12, 14, 16];

export const DraftSettings: React.FC<DraftSettingsProps> = ({
  leagueFormat,
  onFormatChange,
  scoringFormat,
  onScoringChange,
  teamCount,
  onTeamCountChange,
  draftPosition,
  onDraftPositionChange,
}) => {
  const pickNumbers = firstSnakePicks(draftPosition, teamCount, 5);

  return (
    <div className="space-y-8">
      <section>
        <h3 className="mb-3 text-center text-sm font-medium text-zinc-300">Format</h3>
        <div className="hidden sm:block">
          <LeagueFormatSelector
            selectedFormat={leagueFormat}
            onFormatChange={onFormatChange}
            variant="pills"
            className="justify-center"
          />
        </div>
        <div className="sm:hidden">
          <LeagueFormatSelector
            selectedFormat={leagueFormat}
            onFormatChange={onFormatChange}
            variant="dropdown"
          />
        </div>
      </section>

      <section>
        <h3 className="mb-3 text-center text-sm font-medium text-zinc-300">Scoring</h3>
        <div className="flex flex-wrap justify-center gap-2">
          {([
            { id: 'ppr', label: 'PPR' },
            { id: 'half-ppr', label: 'Half PPR' },
            { id: 'standard', label: 'Standard' },
          ] as const).map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => onScoringChange(option.id)}
              className={`rounded-full border px-3 py-1.5 text-sm ${
                scoringFormat === option.id
                  ? 'border-fiesta-blue bg-fiesta-blue/15 text-fiesta-blue'
                  : 'border-white/10 bg-white/5 text-zinc-300 hover:border-white/20'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </section>

      <section>
        <h3 className="mb-3 text-center text-sm font-medium text-zinc-300">Teams</h3>
        <div className="flex justify-center gap-2" role="radiogroup" aria-label="League size">
          {teamOptions.map((count) => (
            <button
              key={count}
              type="button"
              role="radio"
              aria-checked={teamCount === count}
              onClick={() => onTeamCountChange(count)}
              className={`min-w-[3.25rem] rounded-xl border px-4 py-3 text-sm font-semibold ${
                teamCount === count
                  ? 'border-fiesta-yellow bg-fiesta-yellow text-ink'
                  : 'border-white/10 bg-white/5 text-zinc-300 hover:border-white/20'
              }`}
            >
              {count}
            </button>
          ))}
        </div>
      </section>

      <section>
        <h3 className="mb-3 text-center text-sm font-medium text-zinc-300">Your seat</h3>
        <div className="mx-auto flex max-w-lg flex-wrap justify-center gap-2">
          {Array.from({ length: teamCount }, (_, index) => index + 1).map((position) => (
            <button
              key={position}
              type="button"
              onClick={() => onDraftPositionChange(position)}
              className={`h-11 w-11 rounded-xl border text-sm font-semibold ${
                draftPosition === position
                  ? 'border-fiesta-yellow bg-fiesta-yellow text-ink'
                  : 'border-white/10 bg-white/5 text-zinc-300 hover:border-white/20'
              }`}
            >
              {position}
            </button>
          ))}
        </div>
        <p className="mt-3 text-center text-sm text-zinc-500">
          {ordinal(draftPosition)} in odd rounds, {ordinal(teamCount - draftPosition + 1)} in even rounds.
        </p>
      </section>

      <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
        <div className="mb-4 grid grid-cols-3 gap-3 text-center text-sm">
          <div>
            <div className="font-semibold text-white">{formatLabel(leagueFormat)}</div>
            <div className="text-zinc-500">Format</div>
          </div>
          <div>
            <div className="font-semibold text-white">{teamCount}</div>
            <div className="text-zinc-500">Teams</div>
          </div>
          <div>
            <div className="font-semibold text-white">Seat {draftPosition}</div>
            <div className="text-zinc-500">Snake</div>
          </div>
        </div>
        <p className="mb-3 text-center text-sm text-zinc-400">Your first five picks</p>
        <div className="grid grid-cols-5 gap-2">
          {pickNumbers.map((pick, index) => (
            <div key={pick} className="rounded-xl bg-black/30 py-3 text-center">
              <div className="font-semibold text-fiesta-yellow">#{pick}</div>
              <div className="text-xs text-zinc-500">R{index + 1}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
